import { Form, Head, Link, router } from '@inertiajs/react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import InputError from '@/theme1/components/input-error';
import PasskeyVerify from '@/theme1/components/passkey-verify';
import PasswordInput from '@/theme1/components/password-input';
import TextLink from '@/theme1/components/text-link';
import { Button } from '@/theme1/components/ui/button';
import { Checkbox } from '@/theme1/components/ui/checkbox';
import { Input } from '@/theme1/components/ui/input';
import { Label } from '@/theme1/components/ui/label';
import { Spinner } from '@/theme1/components/ui/spinner';

type Props = {
    status?: string;
    canResetPassword: boolean;
    canRegister?: boolean;
    loginAction?: string;
    forgotPasswordUrl?: string;
    redirectTo?: string;
};

const googleLoginUrl = '/user/google/redirect';

export default function Login({
    status,
    canResetPassword,
    canRegister = true,
    loginAction = '/login',
    forgotPasswordUrl = '/forgot-password',
    redirectTo,
}: Props) {
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            const data = event.data as
                | { type?: string; redirect?: string }
                | undefined;

            if (data?.type !== 'captex-google-login-success') {
                return;
            }

            const redirect = data.redirect || '/account';

            if (redirect.startsWith('/admin')) {
                window.location.assign(redirect);

                return;
            }

            router.visit(redirect);
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    return (
        <>
            <Head title="Log in" />

            <section className="relative isolate overflow-hidden bg-[#f6f0e8] py-10 sm:py-14 lg:py-16">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.95),rgba(246,240,232,0.92)_40%,rgba(232,226,214,0.88)_100%)]" />
                <div className="absolute -top-16 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#ead9b4]/35 blur-3xl" />

                <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-white/80 bg-white/90 shadow-[0_30px_90px_rgba(0,0,0,0.12)] backdrop-blur-2xl">
                        <div className="border-b border-neutral-200/70 px-6 py-6 sm:px-8">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.25em] text-neutral-400">Member access</p>
                                    <h1 className="mt-3 text-4xl font-black uppercase tracking-[-0.04em] sm:text-5xl">Sign in</h1>
                                    <p className="mt-3 max-w-xl text-sm leading-6 text-neutral-500">
                                        Use one login for customer and admin access. After login, we will send you to the right panel automatically.
                                    </p>
                                </div>

                                <Link
                                    href="/"
                                    className="hidden rounded-full border border-neutral-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-neutral-500 transition hover:border-neutral-900 hover:text-neutral-900 sm:inline-flex"
                                >
                                    Continue shopping
                                </Link>
                            </div>
                        </div>

                        <div className="px-6 py-6 sm:px-8 sm:py-8">
                            {status && (
                                <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                                    {status}
                                </div>
                            )}

                            <div className="mb-6 flex items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold text-neutral-500">
                                <span className="inline-flex items-center gap-2">
                                    <ShieldCheck className="size-4" />
                                    Secure account sign in
                                </span>
                                <span>Customer or admin</span>
                            </div>

                            <PasskeyVerify />

                            <button
                                type="button"
                                onClick={() => {
                                    window.location.href = googleLoginUrl;
                                }}
                                className="mb-6 flex w-full items-center justify-center gap-3 rounded-full border border-neutral-200 bg-white px-5 py-3.5 text-sm font-semibold text-neutral-700 transition hover:border-neutral-900 hover:text-neutral-900"
                            >
                                <span className="flex size-6 items-center justify-center rounded-full bg-white text-base font-black text-[#4285F4] shadow-[0_1px_2px_rgba(0,0,0,0.12)]">
                                    G
                                </span>
                                Continue with Google
                            </button>

                            <Form
                                action={loginAction}
                                method="post"
                                resetOnSuccess={['password']}
                                className="flex flex-col gap-6"
                            >
                                {({ processing, errors }) => (
                                    <>
                                        {redirectTo && <input type="hidden" name="redirect_to" value={redirectTo} />}
                                        <div className="grid gap-6">
                                            <div className="grid gap-2">
                                                <Label htmlFor="email">Email address</Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    name="email"
                                                    required
                                                    autoFocus
                                                    tabIndex={1}
                                                    autoComplete="email"
                                                    placeholder="email@example.com"
                                                />
                                                <InputError message={errors.email} />
                                            </div>

                                            <div className="grid gap-2">
                                                <div className="flex items-center">
                                                    <Label htmlFor="password">Password</Label>
                                                    {canResetPassword && (
                                                        <TextLink
                                                            href={forgotPasswordUrl}
                                                            className="ml-auto text-sm"
                                                            tabIndex={5}
                                                        >
                                                            Forgot your password?
                                                        </TextLink>
                                                    )}
                                                </div>
                                                <PasswordInput
                                                    id="password"
                                                    name="password"
                                                    required
                                                    tabIndex={2}
                                                    autoComplete="current-password"
                                                    placeholder="Password"
                                                />
                                                <InputError message={errors.password} />
                                            </div>

                                            <div className="flex items-center space-x-3">
                                                <Checkbox
                                                    id="remember"
                                                    name="remember"
                                                    tabIndex={3}
                                                />
                                                <Label htmlFor="remember">Remember me</Label>
                                            </div>

                                            <Button
                                                type="submit"
                                                className="mt-4 w-full"
                                                tabIndex={4}
                                                disabled={processing}
                                                data-test="login-button"
                                            >
                                                {processing && <Spinner />}
                                                Log in
                                                <ArrowRight className="size-4" />
                                            </Button>
                                        </div>

                                        {canRegister && (
                                            <div className="text-center text-sm text-muted-foreground">
                                                Don't have an account?{' '}
                                                <TextLink
                                                    href={redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'}
                                                    tabIndex={5}
                                                >
                                                    Sign up
                                                </TextLink>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
